import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ParentBottomTabBar } from '@/components/ui/ParentBottomTabBar';
import { apiFetch } from '@/services/apiGateway';
import { GlobalHeader } from '@/components/GlobalHeader';
import { AUTHORITIES, COUNTRIES, getSchoolYears, getTrackConfig } from '@/constants/education';
import i18n from '@/i18n';

type ChildMode = 'high_school' | 'university';

interface AddChildResponse {
  child_id: string;
  join_code: string;
  join_code_expires_at: string;
  display_name: string;
}

function formatCode(code: string): string {
  if (code.length === 6) return `${code.slice(0, 3)} - ${code.slice(3)}`;
  return code;
}

function formatExpiry(iso: string, locale: string): string {
  const date = new Date(iso);
  return date.toLocaleString(locale, {
    day: 'numeric', month: 'long', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function ageYears(iso: string): number {
  const dob = new Date(iso);
  const today = new Date();
  return (today.getTime() - dob.getTime()) / (365.25 * 24 * 60 * 60 * 1000);
}

function friendlyError(err: unknown): string {
  const msg = err instanceof Error ? err.message : '';
  const t = i18n.t.bind(i18n);
  if (msg.includes('age between 10 and 25')) return t('add_child.dob_age_range');
  if (msg.includes('Register via POST /parents first')) return t('add_child.err_generic');
  return t('add_child.err_generic');
}

export function AddChildPage() {
  const { t, i18n: i18nInstance } = useTranslation();
  const navigate = useNavigate();

  const [step, setStep] = useState<1 | 2>(1);

  // Form state
  const [displayName, setDisplayName] = useState('');
  const [mode, setMode]               = useState<ChildMode>('high_school');
  const [schoolYear, setSchoolYear]   = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [country, setCountry]         = useState('FR');
  const [selectedAuthorities, setSelectedAuthorities] = useState<string[]>(['MEN']);
  const [authorityFilter, setAuthorityFilter]         = useState('');
  const [specializations, setSpecializations]         = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError]   = useState<string | null>(null);
  const [yearClearedNotice, setYearClearedNotice] = useState(false);

  // Response state
  const [generatedCode, setGeneratedCode] = useState('');
  const [expiry, setExpiry]               = useState('');

  const years = getSchoolYears(country, mode);
  const allAuthorities = AUTHORITIES[country] ?? [];
  const showFilter = allAuthorities.length > 4;
  const filteredAuthorities = showFilter
    ? allAuthorities.filter((a) =>
        a.name.toLowerCase().includes(authorityFilter.toLowerCase()) ||
        a.code.toLowerCase().includes(authorityFilter.toLowerCase()),
      )
    : allAuthorities;
  const trackConfig = mode === 'high_school' ? getTrackConfig(country, schoolYear) : null;

  // Only clears schoolYear (and its dependent specializations) when the
  // current selection genuinely isn't valid for the new country/mode —
  // never silently, always with a visible notice — instead of the old
  // unconditional reset that wiped a still-valid choice with no feedback.
  const clearYearIfInvalid = (validYears: string[]) => {
    setSchoolYear((prev) => {
      if (prev && !validYears.includes(prev)) {
        setSpecializations([]);
        setYearClearedNotice(true);
        return '';
      }
      return prev;
    });
  };

  const handleModeChange = (newMode: ChildMode) => {
    setMode(newMode);
    clearYearIfInvalid(getSchoolYears(country, newMode));
  };

  const handleCountryChange = (newCountry: string) => {
    setCountry(newCountry);
    setSelectedAuthorities([]);
    setAuthorityFilter('');
    clearYearIfInvalid(getSchoolYears(newCountry, mode));
  };

  const handleSchoolYearChange = (newYear: string) => {
    setSchoolYear(newYear);
    setSpecializations([]);
    setYearClearedNotice(false);
  };

  const toggleAuthority = (code: string) => {
    setSelectedAuthorities((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code],
    );
  };

  const toggleSpecialization = (name: string) => {
    setSpecializations((prev) => {
      if (prev.includes(name)) return prev.filter((s) => s !== name);
      if (trackConfig && prev.length >= trackConfig.maxSelect) return prev;
      return [...prev, name];
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!displayName.trim()) return;
    if (!schoolYear) {
      setSubmitError(t('add_child.year_required'));
      return;
    }
    if (selectedAuthorities.length === 0) {
      setSubmitError(t('add_child.authority_required'));
      return;
    }
    if (dateOfBirth) {
      const age = ageYears(dateOfBirth);
      if (age < 10 || age > 25) {
        setSubmitError(t('add_child.dob_age_range'));
        return;
      }
    }
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const res = await apiFetch<AddChildResponse>('/parents/me/children', {
        method: 'POST',
        body: JSON.stringify({
          display_name: displayName.trim(),
          mode,
          school_year: schoolYear,
          date_of_birth: dateOfBirth || undefined,
          country,
          education_authorities: selectedAuthorities,
          specializations,
        }),
      });
      setGeneratedCode(res.join_code);
      setExpiry(res.join_code_expires_at);
      setStep(2);
    } catch (err) {
      if (err instanceof Error && err.message.includes('Register via POST /parents first')) {
        // Signed in but the backend Parent profile was never created (e.g. an
        // earlier API outage interrupted registration). /register detects an
        // active session with no profile and resumes it at the right step.
        navigate('/register', { replace: true });
        return;
      }
      setSubmitError(friendlyError(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(generatedCode);
    } catch {
      const el = document.createElement('textarea');
      el.value = generatedCode;
      el.style.cssText = 'position:fixed;opacity:0';
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
    }
  };

  return (
    <div className="page-center">
      <div className="phone-mockup">
        <GlobalHeader showBack backRoute="/parent" />
        <div className="app-content" style={{ paddingBottom: '24px' }}>

          <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text-dark)', margin: '0 0 20px' }}>
            {t('add_child.title')}
          </h1>

          {/* ── Step 1: Profile Form ── */}
          {step === 1 && (
            <div className="card">
              <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

                {/* First name */}
                <div className="form-group">
                  <label htmlFor="displayName">
                    {t('add_child.first_name')} <span style={{ color: 'var(--danger)' }}>*</span>
                  </label>
                  <input
                    id="displayName"
                    type="text"
                    className="input"
                    placeholder={t('add_child.name_placeholder')}
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    required
                    disabled={isSubmitting}
                  />
                </div>

                {/* Country — determines the year/authority lists below, so it
                    comes before them to avoid a later change silently
                    wiping an already-made selection. */}
                <div className="form-group">
                  <label htmlFor="country">{t('parent_student.country')}</label>
                  <select
                    id="country"
                    className="input"
                    value={country}
                    onChange={(e) => handleCountryChange(e.target.value)}
                    disabled={isSubmitting}
                  >
                    {COUNTRIES.map((c) => (
                      <option key={c.code} value={c.code}>{c.flag} {c.name}</option>
                    ))}
                  </select>
                </div>

                {/* Mode toggle */}
                <div className="form-group">
                  <label>{t('add_child.mode')}</label>
                  <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
                    {(['high_school', 'university'] as ChildMode[]).map((m) => (
                      <button
                        key={m}
                        type="button"
                        onClick={() => handleModeChange(m)}
                        disabled={isSubmitting}
                        style={{
                          flex: 1, padding: '10px', borderRadius: '8px', fontSize: '0.875rem',
                          fontWeight: 600, cursor: 'pointer', border: '2px solid',
                          borderColor: mode === m ? 'var(--primary)' : 'var(--border)',
                          background: mode === m ? 'var(--primary-light)' : 'white',
                          color: mode === m ? 'var(--primary)' : 'var(--text-muted)',
                          transition: 'all 0.15s',
                        }}
                      >
                        {m === 'high_school'
                          ? `🏫 ${t('add_child.high_school')}`
                          : `🎓 ${t('add_child.university')}`}
                      </button>
                    ))}
                  </div>
                </div>

                {/* School year */}
                <div className="form-group">
                  <label htmlFor="schoolYear">
                    {t('add_child.year_of_study')} <span style={{ color: 'var(--danger)' }}>*</span>
                  </label>
                  <select
                    id="schoolYear"
                    className="input"
                    value={schoolYear}
                    onChange={(e) => handleSchoolYearChange(e.target.value)}
                    disabled={isSubmitting}
                  >
                    <option value="">{t('add_child.select_year')}…</option>
                    {years.map((y) => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                  {yearClearedNotice && (
                    <p style={{ fontSize: '0.75rem', color: 'var(--danger)', margin: '4px 0 0' }}>
                      {t('add_child.year_cleared_notice')}
                    </p>
                  )}
                </div>

                {/* Track / specialization multi-select (e.g. French spécialités) */}
                {trackConfig && (
                  <div className="form-group">
                    <label>{trackConfig.label}</label>
                    <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', margin: '0 0 8px' }}>
                      {t('add_child.track_helper', { count: trackConfig.maxSelect })}
                    </p>
                    <div style={{
                      border: '1px solid var(--border)', borderRadius: '8px', padding: '4px 0',
                    }}>
                      {trackConfig.options.map((name) => {
                        const checked = specializations.includes(name);
                        const disabledOption = !checked && specializations.length >= trackConfig.maxSelect;
                        return (
                          <label
                            key={name}
                            style={{
                              display: 'flex', alignItems: 'center', gap: '10px',
                              padding: '8px 12px', cursor: disabledOption ? 'not-allowed' : 'pointer',
                              opacity: disabledOption ? 0.5 : 1,
                              background: checked ? 'var(--primary-light)' : 'transparent',
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleSpecialization(name)}
                              disabled={isSubmitting || disabledOption}
                              style={{ accentColor: 'var(--primary)', width: '16px', height: '16px' }}
                            />
                            <span style={{ fontSize: '0.875rem', color: 'var(--text-dark)' }}>{name}</span>
                          </label>
                        );
                      })}
                    </div>
                    {specializations.length > 0 && (
                      <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', margin: '4px 0 0' }}>
                        {t('add_child.selected', { count: specializations.length })}
                      </p>
                    )}
                  </div>
                )}

                {/* Education authority multi-select */}
                {allAuthorities.length > 0 && (
                  <div className="form-group">
                    <label>
                      {t('add_child.education_authority')} <span style={{ color: 'var(--danger)' }}>*</span>
                    </label>
                    {showFilter && (
                      <input
                        type="text"
                        className="input"
                        placeholder={t('add_child.search_authority')}
                        value={authorityFilter}
                        onChange={(e) => setAuthorityFilter(e.target.value)}
                        disabled={isSubmitting}
                        style={{ marginBottom: '8px' }}
                      />
                    )}
                    <div style={{
                      maxHeight: '180px', overflowY: 'auto',
                      border: '1px solid var(--border)', borderRadius: '8px',
                      padding: '4px 0',
                    }}>
                      {filteredAuthorities.map((a) => (
                        <label
                          key={a.code}
                          style={{
                            display: 'flex', alignItems: 'center', gap: '10px',
                            padding: '8px 12px', cursor: 'pointer',
                            background: selectedAuthorities.includes(a.code) ? 'var(--primary-light)' : 'transparent',
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={selectedAuthorities.includes(a.code)}
                            onChange={() => toggleAuthority(a.code)}
                            disabled={isSubmitting}
                            style={{ accentColor: 'var(--primary)', width: '16px', height: '16px' }}
                          />
                          <span style={{ fontSize: '0.875rem', color: 'var(--text-dark)' }}>
                            {a.name}
                          </span>
                        </label>
                      ))}
                      {filteredAuthorities.length === 0 && (
                        <p style={{ padding: '8px 12px', fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                          {t('parent_student.no_authority_match')}
                        </p>
                      )}
                    </div>
                    {selectedAuthorities.length > 0 && (
                      <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', margin: '4px 0 0' }}>
                        {t('add_child.selected', { count: selectedAuthorities.length })}
                      </p>
                    )}
                  </div>
                )}

                {/* Date of birth */}
                <div className="form-group">
                  <label htmlFor="dateOfBirth">{t('add_child.date_of_birth')}</label>
                  <input
                    id="dateOfBirth"
                    type="date"
                    className="input"
                    value={dateOfBirth}
                    onChange={(e) => setDateOfBirth(e.target.value)}
                    disabled={isSubmitting}
                  />
                  <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', margin: '4px 0 0' }}>
                    {t('add_child.gdpr_note')}
                  </p>
                </div>

                {submitError && (
                  <p style={{
                    color: 'var(--danger)', fontSize: '0.85rem',
                    padding: '10px 14px', background: '#FEF2F2',
                    borderRadius: '8px', margin: 0,
                  }}>
                    ⚠️ {submitError}
                  </p>
                )}

                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isSubmitting || !displayName.trim() || !schoolYear || selectedAuthorities.length === 0}
                  style={{ marginTop: '4px' }}
                >
                  {isSubmitting ? t('actions.creating') : t('add_child.generate_code')}
                </button>

              </form>
            </div>
          )}

          {/* ── Step 2: Join code display (shown once) ── */}
          {step === 2 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>

              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '3rem', marginBottom: '8px' }}>🎉</div>
                <h2 style={{ fontSize: '1.3rem', fontWeight: 700, color: 'var(--text-dark)', margin: '0 0 8px' }}>
                  {t('add_child.profile_created')}
                </h2>
                <p style={{ fontSize: '0.9rem', color: 'var(--text-muted)', margin: 0, lineHeight: 1.6 }}>
                  {t('add_child.share_code_desc', { name: displayName })}
                </p>
              </div>

              <div style={{
                fontSize: '2.5rem', letterSpacing: '4px', textAlign: 'center',
                background: 'var(--bg-light)', padding: '32px', borderRadius: '16px',
                fontWeight: 700, color: 'var(--primary)', fontFamily: 'monospace',
                userSelect: 'none',
              }}>
                {formatCode(generatedCode)}
              </div>

              <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', textAlign: 'center', margin: 0 }}>
                {expiry ? t('add_child.valid_until', { date: formatExpiry(expiry, i18nInstance.language) }) : t('add_child.valid_72h')} · {t('add_child.shown_once')}
              </p>

              <button
                type="button"
                className="btn btn-outline"
                onClick={handleCopy}
                style={{ width: '100%' }}
              >
                {t('actions.copy')}
              </button>

              <button
                type="button"
                className="btn btn-primary"
                onClick={() => navigate('/parent')}
                style={{ width: '100%' }}
              >
                {t('add_child.done_btn')}
              </button>

            </div>
          )}

        </div>

        <ParentBottomTabBar activeTab="Family" />
      </div>
    </div>
  );
}
